import { useEffect, useState, type ReactNode } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import {
  BadgeCheck,
  Scale,
  ChevronsLeft,
  ChevronsRight,
  Globe,
  Keyboard,
  Coins,
  Store,
  Star,
  Tv,
  Music,
  Hash,
  Smile,
  LayoutDashboard,
  LayoutGrid,
  LifeBuoy,
  MessagesSquare,
  Megaphone,
  LogOut,
  Moon,
  Search,
  Shield,
  ShieldAlert,
  ShieldCheck,
  SlidersHorizontal,
  Sun,
  Users,
  UsersRound,
  Boxes,
  Database,
  Gauge,
} from 'lucide-react'
import { cn } from '@/lib/cn'
import { useI18n, useT } from '@/i18n'
import { LANG_LABELS, type Lang } from '@/i18n'
import { useAuth } from '@/providers/AuthProvider'
import { useTheme } from '@/providers/UiProviders'
import { PERMISSIONS, type Permission } from '@/lib/permissions'
import { Button } from '@/components/ui/button'
import { Menu, MenuItem, MenuLabel, MenuSeparator } from '@/components/ui/menu'
import { CommandPalette } from './CommandPalette'
import { ShortcutsDialog } from './ShortcutsDialog'

/* ── Muhit ────────────────────────────────────────────────────────
   SKILL §5: prod'dan boshqa muhitda topbar bo'ylab rangli chiziq.
   Prod'da chiziq YO'Q. Bu «xato muhitda ish qilish» xatosini oldini oladi. */

export type Environment = 'prod' | 'staging' | 'dev'

export function detectEnvironment(): Environment {
  const h = window.location.hostname
  if (h === 'admin.mygram.my') return 'prod'
  if (h.startsWith('staging.')) return 'staging'
  return 'dev'
}

function EnvironmentBar({ env }: { env: Environment }) {
  const t = useT()
  if (env === 'prod') return null
  const color = env === 'staging' ? 'var(--env-staging)' : 'var(--env-dev)'
  return (
    <div
      className="flex h-5 shrink-0 items-center justify-center text-xs font-semibold tracking-wide text-black uppercase"
      style={{ background: color }}
    >
      {env === 'staging' ? t.env.staging : t.env.dev}
    </div>
  )
}

/* ── Navigatsiya ──────────────────────────────────────────────── */

interface NavItem {
  to: string
  label: string
  icon: ReactNode
  permission?: Permission
}

const SIDEBAR_KEY = 'grom_admin_sidebar'

export function AppShell({ children }: { children: ReactNode }) {
  const t = useT()
  const { lang, setLang } = useI18n()
  const { theme, setTheme } = useTheme()
  const { identity, signOut, can } = useAuth()
  const navigate = useNavigate()

  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_KEY) === '1'
    } catch {
      return false
    }
  })
  const [paletteOpen, setPaletteOpen] = useState(false)
  const [shortcutsOpen, setShortcutsOpen] = useState(false)

  const env = detectEnvironment()

  function toggleSidebar() {
    setCollapsed((v) => {
      const next = !v
      try {
        localStorage.setItem(SIDEBAR_KEY, next ? '1' : '0')
      } catch {
        /* localStorage yopiq */
      }
      return next
    })
  }

  // Global yorliqlar (SKILL §6). `g h` kabi ketma-ketliklar ham.
  useEffect(() => {
    let chord = false
    let chordTimer: number | undefined

    function onKey(e: KeyboardEvent) {
      const el = e.target as HTMLElement | null
      const typing =
        el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable)

      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setPaletteOpen(true)
        return
      }
      if ((e.metaKey || e.ctrlKey) && e.key === '\\') {
        e.preventDefault()
        toggleSidebar()
        return
      }
      if (typing) return

      if (e.key === '?') {
        e.preventDefault()
        setShortcutsOpen(true)
        return
      }
      if (e.key === 'g') {
        chord = true
        window.clearTimeout(chordTimer)
        chordTimer = window.setTimeout(() => (chord = false), 1200)
        return
      }
      if (chord) {
        chord = false
        if (e.key === 'h') navigate('/')
        else if (e.key === 's') navigate('/staff')
      }
    }

    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('keydown', onKey)
      window.clearTimeout(chordTimer)
    }
  }, [navigate])

  const items: NavItem[] = [
    { to: '/', label: t.nav.dashboard, icon: <LayoutDashboard size={15} /> },
    { to: '/modules', label: t.nav.modules, icon: <LayoutGrid size={15} /> },
    { to: '/ops-hub', label: 'Операции', icon: <Gauge size={15} /> }, // lane-A: function-grouped hub of ops/security/compliance/support subsystems
    { to: '/users', label: t.nav.users, icon: <Users size={15} />, permission: PERMISSIONS.USERS_VIEW },
    { to: '/groups', label: t.nav.groups, icon: <MessagesSquare size={15} />, permission: PERMISSIONS.USERS_VIEW },
    { to: '/moderation', label: t.nav.moderation, icon: <ShieldAlert size={15} />, permission: PERMISSIONS.USERS_VIEW },
    { to: '/support', label: t.nav.support, icon: <LifeBuoy size={15} />, permission: PERMISSIONS.USERS_VIEW },
    { to: '/verification', label: t.nav.verification, icon: <BadgeCheck size={15} />, permission: PERMISSIONS.USERS_VIEW },
    { to: '/finance', label: t.nav.finance, icon: <Coins size={15} />, permission: PERMISSIONS.USERS_VIEW },
    { to: '/market/sellers', label: t.market.title, icon: <Store size={15} />, permission: PERMISSIONS.USERS_VIEW },
    { to: '/points/earn-rules', label: t.points.title, icon: <Star size={15} />, permission: PERMISSIONS.USERS_VIEW },
    { to: '/media/tv', label: 'ТВ', icon: <Tv size={15} />, permission: PERMISSIONS.USERS_VIEW },
    { to: '/media/music', label: 'Музыка', icon: <Music size={15} />, permission: PERMISSIONS.USERS_VIEW },
    { to: '/media/numbers', label: 'VIP рақамлар', icon: <Hash size={15} />, permission: PERMISSIONS.USERS_VIEW },
    { to: '/stickers/moderation', label: t.stickers.moderation, icon: <Smile size={15} />, permission: PERMISSIONS.USERS_VIEW },
    { to: '/geo', label: t.nav.geo, icon: <Globe size={15} />, permission: PERMISSIONS.COUNTRIES_VIEW },
    { to: '/staff', label: t.nav.staff, icon: <UsersRound size={15} />, permission: PERMISSIONS.STAFF_VIEW },
    // `/security` (aniq tabsiz) — shunda ichki tab almashganda ham
    // yon paneldagi element faol qoladi (NavLink prefiks bo'yicha mos keladi).
    { to: '/security', label: t.nav.security, icon: <Shield size={15} />, permission: PERMISSIONS.AUDIT_VIEW },
    { to: '/legal', label: t.nav.legal, icon: <Scale size={15} />, permission: PERMISSIONS.USERS_VIEW },
    { to: '/broadcasts', label: t.nav.broadcast, icon: <Megaphone size={15} />, permission: PERMISSIONS.USERS_VIEW },
    { to: '/settings/system', label: t.nav.settings, icon: <SlidersHorizontal size={15} />, permission: PERMISSIONS.USERS_VIEW }, // A24.01
    { to: '/ads/advertisers', label: 'Реклама', icon: <Megaphone size={15} />, permission: PERMISSIONS.USERS_VIEW }, // A20 reklama (lane C ekranlari + live backend)
    { to: '/platform/modules', label: 'Модули', icon: <Boxes size={15} />, permission: PERMISSIONS.AUDIT_VIEW }, // A0 platforma kill-switch (superadmin)
    { to: '/platform/db', label: 'Модели (БД)', icon: <Database size={15} />, permission: PERMISSIONS.AUDIT_VIEW }, // A0 DB modellar ko'ruvchi (superadmin, read-only)
  ]

  // rbac-ui.md: sidebar bandlari huquq bo'yicha filtrlanadi.
  const visible = items.filter((i) => !i.permission || can(i.permission))

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-surface-1">
      <EnvironmentBar env={env} />

      {/* Topbar 48px */}
      <header
        className="flex shrink-0 items-center gap-3 border-b border-border-default bg-surface-2 px-3"
        style={{ height: 'var(--topbar-h)' }}
      >
        <div className="flex items-center gap-2">
          <span
            className="flex h-6 w-6 items-center justify-center rounded-md text-sm"
            style={{ background: 'var(--accent)', color: 'var(--accent-fg)' }}
            aria-hidden
          >
            ⚡
          </span>
          <span className="text-md font-semibold text-primary">{t.common.appName}</span>
        </div>

        <button
          type="button"
          onClick={() => setPaletteOpen(true)}
          className={cn(
            'ml-4 flex h-7 w-80 items-center gap-2 rounded-md border border-border-default bg-surface-1 px-2',
            'text-sm text-tertiary hover:border-border-strong hover:text-secondary',
          )}
        >
          <Search size={13} />
          <span className="flex-1 text-left">{t.common.search}</span>
          <kbd className="rounded-sm border border-border-default px-1 font-mono text-xs">⌘K</kbd>
        </button>

        <div className="flex-1" />

        <Button variant="ghost" size="icon" onClick={() => setShortcutsOpen(true)} title={t.shortcuts.title}>
          <Keyboard size={15} />
        </Button>

        <Button
          variant="ghost"
          size="icon"
          onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
          title={t.common.theme}
        >
          {theme === 'dark' ? <Sun size={15} /> : <Moon size={15} />}
        </Button>

        <Menu
          align="end"
          width={220}
          trigger={({ toggle, ref }) => (
            <Button ref={ref} variant="ghost" size="md" onClick={toggle}>
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-surface-3 text-xs">
                {identity?.username?.charAt(0).toUpperCase() ?? '?'}
              </span>
              <span className="max-w-32 truncate">{identity?.username}</span>
            </Button>
          )}
        >
          {(close) => (
            <>
              <MenuLabel>{identity?.role ?? t.common.unknown}</MenuLabel>
              <MenuSeparator />
              <MenuItem
                onSelect={() => {
                  navigate('/account')
                  close()
                }}
              >
                {t.account.profile}
              </MenuItem>
              <MenuItem
                onSelect={() => {
                  navigate('/account/interface')
                  close()
                }}
              >
                {t.account.interface}
              </MenuItem>
              <MenuItem
                icon={<Keyboard size={13} />}
                onSelect={() => {
                  navigate('/account/shortcuts')
                  close()
                }}
              >
                {t.account.shortcuts}
              </MenuItem>
              <MenuItem
                onSelect={() => {
                  navigate('/account/help')
                  close()
                }}
              >
                {t.account.help}
              </MenuItem>
              <MenuSeparator />
              <MenuLabel>{t.common.language}</MenuLabel>
              {(Object.keys(LANG_LABELS) as Lang[]).map((l) => (
                <MenuItem
                  key={l}
                  onSelect={() => {
                    setLang(l)
                    close()
                  }}
                  trailing={lang === l ? '✓' : undefined}
                >
                  {LANG_LABELS[l]}
                </MenuItem>
              ))}
              <MenuSeparator />
              <MenuItem icon={<LogOut size={13} />} danger onSelect={signOut}>
                {t.common.signOut}
              </MenuItem>
            </>
          )}
        </Menu>
      </header>

      <div className="flex min-h-0 flex-1">
        {/* Sidebar — holati foydalanuvchi bo'yicha saqlanadi (SKILL §5) */}
        <nav
          className="flex shrink-0 flex-col border-r border-border-default bg-surface-2 py-2"
          style={{ width: collapsed ? 'var(--sidebar-w-collapsed)' : 'var(--sidebar-w)' }}
        >
          <div className="flex-1 space-y-0.5 px-2">
            {visible.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                title={collapsed ? item.label : undefined}
                className={({ isActive }) =>
                  cn(
                    'flex h-8 items-center gap-2.5 rounded-md px-2 text-base transition-colors',
                    isActive
                      ? 'bg-accent-subtle font-medium text-accent'
                      : 'text-secondary hover:bg-surface-3 hover:text-primary',
                    collapsed && 'justify-center px-0',
                  )
                }
              >
                <span className="shrink-0">{item.icon}</span>
                {!collapsed && <span className="truncate">{item.label}</span>}
              </NavLink>
            ))}
          </div>

          <div className="px-2">
            <Button
              variant="ghost"
              size={collapsed ? 'icon' : 'md'}
              onClick={toggleSidebar}
              className={cn('w-full', collapsed && 'w-8')}
              title={collapsed ? t.nav.expand : t.nav.collapse}
            >
              {collapsed ? <ChevronsRight size={14} /> : <ChevronsLeft size={14} />}
              {!collapsed && <span className="truncate">{t.nav.collapse}</span>}
            </Button>
          </div>
        </nav>

        {/* Kontent — kengligi CHEKLANMAYDI (SKILL §5: max-w-7xl bu yerda xato) */}
        <main className="flex min-w-0 flex-1 flex-col overflow-hidden">{children}</main>
      </div>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} items={visible} />
      <ShortcutsDialog open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />
    </div>
  )
}

/** Sahifa sarlavhasi + amallar (56px, SKILL §5). */
export function PageHeader({
  title,
  screenId,
  description,
  actions,
  verified,
}: {
  title: string
  /** TZ ekran ID'si — ekranda ko'rinadi, qabul mezonini tekshirishni osonlashtiradi. */
  screenId: string
  description?: string
  actions?: ReactNode
  /** Jonli backend bilan tasdiqlangan ekran. */
  verified?: boolean
}) {
  return (
    <div
      className="flex shrink-0 items-center gap-3 border-b border-border-default px-3"
      style={{ minHeight: 'var(--pageheader-h)' }}
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <h1 className="truncate text-lg font-semibold text-primary">{title}</h1>
          <span
            className="rounded-sm border border-border-subtle px-1 font-mono text-xs text-tertiary"
            title="TZ ID"
          >
            {screenId}
          </span>
          {verified && <ShieldCheck size={13} className="text-success" aria-label="live" />}
        </div>
        {description && <p className="truncate text-sm text-secondary">{description}</p>}
      </div>
      <div className="flex-1" />
      {actions}
    </div>
  )
}
